import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import usePhotoProfilingConsent from '../hooks/usePhotoProfilingConsent';
import {ShieldIcon} from './icons';

/**
 * Барьер согласия на AI-профилирование фото (GDPR ст.22).
 * Оборачивает страницы, которые требуют photoProfilingConsent.
 * При входе проверяет согласие — если не выдано, показывает экран с объяснением
 * и кнопками «Принять» / «Вернуться назад».
 */
const ConsentGate: React.FC<{children: React.ReactNode}> = ({children}) => {
    const {granted, loading, saving, setGranted} = usePhotoProfilingConsent();
    const navigate = useNavigate();
    const [error, setError] = useState(false);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <span className="animate-spin w-8 h-8 border-4 border-brand-500/30 border-t-brand-500 rounded-full"/>
            </div>
        );
    }

    if (granted) {
        return <>{children}</>;
    }

    const accept = async () => {
        setError(false);
        const ok = await setGranted(true);
        if (!ok) setError(true);
    };

    return (
        <div className="flex items-center justify-center py-16 px-4">
            <div className="w-full max-w-md bg-white dark:bg-ink-900 rounded-2xl shadow-xl p-8 space-y-6">
                {/* Icon */}
                <div className="flex justify-center">
                    <div className="w-16 h-16 rounded-2xl bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center">
                        <ShieldIcon className="w-8 h-8 text-brand-600 dark:text-brand-400"/>
                    </div>
                </div>

                {/* Title */}
                <div className="text-center space-y-2">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        Требуется согласие
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Эта функция использует AI-анализ фотографий и требует вашего явного согласия.
                    </p>
                </div>

                {/* Details */}
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <li className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0 text-brand-500">✓</span>
                        Обрабатываются фото вещей, их характеристики и сопроводительный текст.
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0 text-brand-500">✓</span>
                        Цель: распознавание одежды, подбор образов и рекомендации.
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0 text-brand-500">✓</span>
                        Лица людей и биометрия <strong>не обрабатываются</strong> — только вещи.
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0 text-brand-500">✓</span>
                        Согласие можно отозвать в любой момент в{' '}
                        <button
                            onClick={() => navigate('/profile/privacy')}
                            className="underline text-brand-600 dark:text-brand-400 hover:no-underline"
                        >
                            настройках приватности
                        </button>.
                    </li>
                </ul>

                {error && (
                    <p className="text-sm text-red-600 dark:text-red-400 text-center">
                        Не удалось сохранить согласие. Попробуйте ещё раз.
                    </p>
                )}

                {/* Actions */}
                <div className="flex flex-col gap-3 pt-1">
                    <button
                        onClick={accept}
                        disabled={saving}
                        className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {saving && (
                            <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"/>
                        )}
                        {saving ? 'Сохранение…' : 'Принять и продолжить'}
                    </button>
                    <button
                        onClick={() => navigate(-1)}
                        disabled={saving}
                        className="w-full btn-secondary py-3 disabled:opacity-50"
                    >
                        Вернуться назад
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConsentGate;
